<script setup>
import { ref, nextTick, onBeforeUnmount } from 'vue';
import { Chrome } from '@lk77/vue3-color';
import { onClickOutside } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';
import { anchoredPanelStyle } from 'dashboard/helper/anchoredPanel';

defineProps({
  modelValue: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const isPickerOpen = ref(false);
const panelStyle = ref({});
const pickerRef = ref(null);
const panelRef = ref(null);

const positionPanel = () => {
  panelStyle.value = anchoredPanelStyle(pickerRef.value, panelRef.value?.$el);
};

// O painel é `fixed`: precisa acompanhar QUALQUER container que role (captura),
// não só a janela.
const watchViewport = () => {
  window.addEventListener('scroll', positionPanel, true);
  window.addEventListener('resize', positionPanel);
};

const unwatchViewport = () => {
  window.removeEventListener('scroll', positionPanel, true);
  window.removeEventListener('resize', positionPanel);
};

const toggleColorPicker = () => {
  isPickerOpen.value = !isPickerOpen.value;
  if (isPickerOpen.value) {
    positionPanel();
    // Segunda passada com o painel já no DOM, para medir o tamanho real.
    nextTick(positionPanel);
    watchViewport();
  } else {
    unwatchViewport();
  }
};

const closeTogglePicker = () => {
  if (isPickerOpen.value) {
    toggleColorPicker();
  }
};

const updateColor = e => {
  emit('update:modelValue', e.hex);
};

// Fecha só em clique REALMENTE fora. O quadro Chrome agora vive no <body>
// (Teleport, para não ser recortado por cartão com overflow-hidden), então
// deixou de estar dentro do pickerRef — sem o `ignore`, cada clique DENTRO do
// seletor contaria como "fora" e ele fecharia ao primeiro ajuste de cor.
onClickOutside(pickerRef, closeTogglePicker, { ignore: [panelRef] });

onBeforeUnmount(unwatchViewport);
</script>

<template>
  <div ref="pickerRef" class="relative w-fit">
    <Button
      color="slate"
      icon="i-lucide-pipette"
      trailing-icon
      class="!px-3 !py-3 [&>svg]:w-4 [&>svg]:h-4"
      @click="toggleColorPicker"
    >
      <div class="flex items-center flex-grow gap-2">
        <span
          class="rounded-md size-4"
          :style="{ backgroundColor: modelValue }"
        />
        <span class="min-w-0 truncate">{{ modelValue }}</span>
      </div>
    </Button>
    <Teleport to="body">
      <Chrome
        v-if="isPickerOpen"
        ref="panelRef"
        disable-alpha
        :model-value="modelValue"
        class="colorpicker--chrome"
        :style="panelStyle"
        @update:model-value="updateColor"
      />
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.colorpicker--chrome.vc-chrome {
  @apply shadow-lg fixed bg-n-background z-[9999] border border-n-weak dark:border-n-weak rounded-[8px];

  :deep() {
    .vc-chrome-saturation-wrap {
      @apply rounded-t-[7px];

      .vc-saturation {
        @apply rounded-t-[8px];
      }
    }

    .vc-chrome-body {
      @apply rounded-b-[7px] bg-n-alpha-3;

      .vc-chrome-toggle-btn {
        .vc-chrome-toggle-icon svg {
          @apply [&>path]:fill-n-slate-10 dark:[&>path]:fill-n-slate-10 left-3 relative;
        }
        .vc-chrome-toggle-icon-highlight {
          @apply bg-n-background;
        }
      }
    }

    input,
    .vc-input__input {
      @apply bg-n-background text-n-slate-12 rounded-md shadow-none;
    }

    .vc-input__label {
      @apply text-n-slate-11 dark:text-n-slate-11;
    }
  }
}
</style>
